import React from 'react'
import ServicesGallerySection from '../components/sections/ServicesGallerySection'

const ServicesGalleryDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Services Gallery Section
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Elegant services showcase with portfolio image display based on Figma design. 
            Features split-screen layout, animated content, and professional portfolio presentation.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Perfect Split Layout</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• 50/50 content/image split</li>
                <li>• 600px exact width divisions</li>
                <li>• Vertical divider line</li>
                <li>• Professional presentation</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Visual Impact</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Large portfolio image showcase</li>
                <li>• Smooth entrance animations</li>
                <li>• Overlay content options</li>
                <li>• Mobile responsive design</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Hudson Club Services</h3>
              <ul className="text-gray-600 space-y-2 text-sm">
                <li>• Home management focus</li>
                <li>• Service categorization</li>
                <li>• Portfolio case studies</li>
                <li>• Professional credentials</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Component Showcase */}
      <ServicesGallerySection />

      {/* Component Variations */}
      <div className="bg-gray-50 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Design Specifications & Usage
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Layout Specs */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                🎨 Layout Specifications
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Section Height</span>
                  <span className="text-gray-600">600px</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Container Width</span>
                  <span className="text-gray-600">1200px max</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Content Width</span>
                  <span className="text-gray-600">600px</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Image Width</span>
                  <span className="text-gray-600">960px</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="font-medium text-gray-700">Background</span>
                  <span className="text-gray-600">rgb(246, 246, 241)</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="font-medium text-gray-700">Text Color</span>
                  <span className="text-gray-600">rgb(30, 29, 28)</span>
                </div>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                ⚡ Key Features
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Split-Screen Design</p>
                    <p className="text-gray-600 text-sm">Perfect content-image balance</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Portfolio Showcase</p>
                    <p className="text-gray-600 text-sm">Large visual impact area</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-purple-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Service Categories</p>
                    <p className="text-gray-600 text-sm">Organized service offerings</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Animated Elements</p>
                    <p className="text-gray-600 text-sm">Smooth entrance effects</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-3 mt-1 flex-shrink-0"></div>
                  <div>
                    <p className="font-medium text-gray-900">Mobile Responsive</p>
                    <p className="text-gray-600 text-sm">Optimized for all devices</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Perfect Use Cases */}
          <div className="bg-white rounded-lg p-8 shadow-sm mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              🎯 Perfect Use Cases
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-blue-600 text-xl">🏠</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Portfolio Pages</h4>
                <p className="text-gray-600 text-sm">Showcase work examples and case studies</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 text-xl">⚙️</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Services Overview</h4>
                <p className="text-gray-600 text-sm">Highlight key service categories</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-purple-600 text-xl">📋</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Case Studies</h4>
                <p className="text-gray-600 text-sm">Present detailed project results</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-yellow-600 text-xl">🎨</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Before/After</h4>
                <p className="text-gray-600 text-sm">Show transformation results</p>
              </div>
            </div>
          </div>

          {/* Integration Example */}
          <div className="bg-gray-900 rounded-lg p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">
              📝 Simple Integration
            </h3>
            
            <pre className="bg-gray-800 rounded p-4 text-sm overflow-x-auto mb-6">
              <code>{`import ServicesGallerySection from '../components/sections/ServicesGallerySection'

const ServicesPage = () => {
  return (
    <div>
      <ServicesGallerySection />
    </div>
  )
}`}</code>
            </pre>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
              <div>
                <h4 className="font-semibold mb-3 text-gray-300">Customization Options:</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>• Replace background image</li>
                  <li>• Modify service categories</li>
                  <li>• Update overlay content</li>
                  <li>• Customize CTA button</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-gray-300">Image Requirements:</h4>
                <ul className="space-y-2 text-gray-400">
                  <li>• Minimum: 960x600px</li>
                  <li>• Format: JPG, PNG, WebP</li>
                  <li>• Aspect ratio: 16:10</li>
                  <li>• High quality preferred</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServicesGalleryDemoPage 